<template>
  <div class="image-card">
    <div class="image-card-photo">
      <img :src="src" v-focal="focal" :alt="alt"/>
    </div>
    <div class="image-card-footer">
      <div class="rel-key" :class="{ 'rel-key-highlight': highlightRelationship }">
        <span v-for="(step, i) in stepsRef" :key="i" class="rel-square" :class="step"></span>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
// a photo card with the relationship footer beneath it. Footer sizes use container-query units,
// so an ancestor must be a query container; tune via the CSS vars below.
.image-card {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #fff;
  // --card-border-width is the shared "large card" knob (default 1px); an ancestor bumps it to
  // thicken the border for the enlarged setup / flipped-play layout without restyling the card.
  border: var(--card-border-width, 1px) solid #ccc;
  border-radius: var(--image-card-radius, 12px);
  box-shadow: var(--image-card-shadow, 0 2px 10px rgba(0, 0, 0, 0.2));
  overflow: hidden;
}

.image-card-photo {
  position: relative;
  flex-grow: 1;
  overflow: hidden;

  img {
    // sizing and placement are applied by v-focal
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.image-card-footer {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  padding: var(--rel-pad, 4cqi);
}

// groups the relationship squares so a highlight box can hug just them (see rel-key-highlight).
// Uses an outline, not a border, so the box takes no layout space and never shifts the squares.
.rel-key {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: var(--rel-gap, 2.5cqi);
  border-radius: 0.75cqi;
  // offset + width must stay within the footer's 4cqi padding so the box isn't clipped by the
  // card's overflow:hidden (which rounds the corners and can't simply be turned off)
  outline: 2cqi solid transparent;
  outline-offset: 1.5cqi;
  transition: outline-color 0.7s ease;
}

.rel-key-highlight {
  outline-color: #dc2626;
}

.rel-square {
  width: var(--rel-size, 6cqi);
  height: var(--rel-size, 6cqi);
  border-radius: calc(var(--rel-size, 6cqi) * 0.25);

  &.f {
    background: #73a7f0;
  }

  &.m {
    background: #f2c1d6;
  }
}
</style>

<script setup lang="ts">
import { computed } from 'vue'
import { vFocal, Focal } from '../vFocal'

const props = defineProps<{
  src: string
  focal?: Focal
  relationship?: string
  alt?: string
  highlightRelationship?: boolean
}>()

// relationship path from the root person: F (father) = blue square, M (mother) = pink square
const stepsRef = computed(() =>
  [...(props.relationship || '')].map(step => (step === 'F' ? 'f' : 'm')))
</script>
